import React, { useState, useRef } from 'react';
import { 
  Video, Upload, Download, RefreshCw, AlertCircle, 
  ArrowLeft, Sliders, CheckCircle2, Film, Play, Monitor, Gauge, Sparkles
} from 'lucide-react';

interface VideoRendererProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
}

export default function VideoRenderer({ language, onBack }: VideoRendererProps) {
  const isUk = language === 'uk';
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);

  // Settings states
  const [width, setWidth] = useState(960);
  const [height, setHeight] = useState(540);
  const [fps, setFps] = useState(30);
  const [duration, setDuration] = useState(22);
  
  // Rendering status states
  const [isRendering, setIsRendering] = useState(false);
  const [progressLogs, setProgressLogs] = useState<string[]>([]);
  const [progressPercent, setProgressPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const logEndRef = useRef<HTMLDivElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const ext = selectedFile.name.split('.').pop()?.toLowerCase();
    if (ext !== 'html' && ext !== 'zip') {
      setError(isUk ? 'Допускаються тільки файли .html або .zip' : 'Only .html or .zip files are allowed');
      return;
    }

    setFile(selectedFile);
    setFileName(selectedFile.name);
    setFileSize(selectedFile.size);
    setError(null);
    setDownloadUrl(null);
    setProgressLogs([]);
    setProgressPercent(0);
  };

  const handleStartRender = async () => {
    if (!file) return;

    setIsRendering(true);
    setError(null);
    setDownloadUrl(null);
    setProgressLogs([isUk ? 'Підготовка до завантаження файлу...' : 'Preparing file upload...']);
    setProgressPercent(0);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('width', width.toString());
    formData.append('height', height.toString());
    formData.append('fps', fps.toString());
    formData.append('duration', duration.toString());

    try {
      const response = await fetch('/api/render-video', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(errorText.error || 'Server error occurred during upload');
      }

      // Establish Server-Sent Events stream from the response body (readable stream)
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error(isUk ? 'Не вдалося відкрити потік даних з сервера.' : 'Failed to open event stream from server.');
      }

      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        
        // Save the incomplete last line back to buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim() || !line.startsWith('data: ')) continue;
          
          try {
            const rawData = JSON.parse(line.replace('data: ', ''));
            const { event, message, error: errStr, downloadUrl: dlUrl } = rawData;

            if (event === 'progress' && message) {
              setProgressLogs(prev => {
                const nextLogs = [...prev, message];
                // Keep only last 100 log lines to avoid DOM lag
                if (nextLogs.length > 100) nextLogs.shift();
                return nextLogs;
              });

              // Scroll to bottom of logs
              setTimeout(() => {
                logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              }, 50);

              // Parse progress percent
              const match = message.match(/\((\d+)%\)/);
              if (match) {
                setProgressPercent(parseInt(match[1], 10));
              }
            } else if (event === 'complete' && dlUrl) {
              setProgressPercent(100);
              setProgressLogs(prev => [...prev, isUk ? '🎉 Рендеринг завершено успішно!' : '🎉 Rendering completed successfully!']);
              setDownloadUrl(dlUrl);
              setIsRendering(false);
            } else if (event === 'error') {
              throw new Error(errStr || 'An error occurred during video generation.');
            }
          } catch (e: any) {
            console.error('Error parsing SSE event:', e);
          }
        }
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Помилка рендерингу відео.' : 'Error occurred during video rendering.'));
      setIsRendering(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setFileName(null);
    setFileSize(null);
    setError(null);
    setDownloadUrl(null);
    setProgressLogs([]);
    setProgressPercent(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in font-sans">
      
      {/* Go Back & Title Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4 print:hidden">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>{isUk ? 'Повернутися на головну сторінку' : 'Return to Main Page'}</span>
        </button>
        
        <div className="text-right">
          <span className="text-[9px] font-bold font-mono uppercase bg-green-50 border border-green-200 px-2 py-0.5 rounded text-green-600">
            {isUk ? 'МЕДІА & РЕНДЕР' : 'MEDIA & RENDER'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        
        {/* Settings and File Upload Column */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 shadow-xs space-y-5">
            <div className="flex items-center gap-2 text-slate-800 font-bold border-b border-slate-100 pb-3">
              <Sliders className="w-4 h-4 text-green-500" />
              <h2 className="text-sm uppercase tracking-wider">{isUk ? 'Параметри рендерингу' : 'Rendering Parameters'}</h2>
            </div>

            {/* Resolution Selector */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-600">{isUk ? 'Роздільна здатність відео' : 'Video Resolution'}</label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => { setWidth(960); setHeight(540); }}
                  className={`py-1.5 px-2 text-[11px] font-mono border rounded transition-all ${
                    width === 960 && height === 540
                      ? 'bg-green-50 border-green-300 text-green-700 font-bold'
                      : 'bg-slate-50 border-slate-200 text-slate-650 hover:bg-slate-100'
                  }`}
                  disabled={isRendering}
                >
                  960x540 (Default)
                </button>
                <button
                  type="button"
                  onClick={() => { setWidth(1280); setHeight(720); }}
                  className={`py-1.5 px-2 text-[11px] font-mono border rounded transition-all ${
                    width === 1280 && height === 720
                      ? 'bg-green-50 border-green-300 text-green-700 font-bold'
                      : 'bg-slate-50 border-slate-200 text-slate-650 hover:bg-slate-100'
                  }`}
                  disabled={isRendering}
                >
                  1280x720 (720p)
                </button>
                <button
                  type="button"
                  onClick={() => { setWidth(1920); setHeight(1080); }}
                  className={`py-1.5 px-2 text-[11px] font-mono border rounded transition-all ${
                    width === 1920 && height === 1080
                      ? 'bg-green-50 border-green-300 text-green-700 font-bold'
                      : 'bg-slate-50 border-slate-200 text-slate-650 hover:bg-slate-100'
                  }`}
                  disabled={isRendering}
                >
                  1920x1080 (1080p)
                </button>
              </div>

              {/* Custom Input */}
              <div className="grid grid-cols-2 gap-2 pt-1">
                <div className="flex items-center gap-1.5 border border-slate-200 rounded px-2.5 py-1">
                  <span className="text-[10px] text-slate-400 font-bold font-mono">W:</span>
                  <input
                    type="number"
                    value={width}
                    onChange={(e) => setWidth(Math.max(100, parseInt(e.target.value, 10) || 0))}
                    className="w-full bg-transparent text-xs font-mono focus:outline-hidden text-slate-700"
                    disabled={isRendering}
                  />
                </div>
                <div className="flex items-center gap-1.5 border border-slate-200 rounded px-2.5 py-1">
                  <span className="text-[10px] text-slate-400 font-bold font-mono">H:</span>
                  <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(Math.max(100, parseInt(e.target.value, 10) || 0))}
                    className="w-full bg-transparent text-xs font-mono focus:outline-hidden text-slate-700"
                    disabled={isRendering}
                  />
                </div>
              </div>
            </div>

            {/* FPS Selector */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-slate-600 flex items-center gap-1">
                <Gauge className="w-3.5 h-3.5 text-slate-450" />
                <span>{isUk ? 'Частота кадрів (FPS)' : 'Frames Per Second (FPS)'}</span>
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-xs text-slate-650 cursor-pointer">
                  <input
                    type="radio"
                    checked={fps === 30}
                    onChange={() => setFps(30)}
                    disabled={isRendering}
                    className="accent-green-500"
                  />
                  <span className={fps === 30 ? 'font-bold text-slate-800' : ''}>30 FPS ({isUk ? 'стандартна плавність' : 'standard smoothness'})</span>
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-650 cursor-pointer">
                  <input
                    type="radio"
                    checked={fps === 60}
                    onChange={() => setFps(60)}
                    disabled={isRendering}
                    className="accent-green-500"
                  />
                  <span className={fps === 60 ? 'font-bold text-slate-800' : ''}>60 FPS ({isUk ? 'максимальна плавність' : 'max smoothness'})</span>
                </label>
              </div>
            </div>

            {/* Duration setting */}
            <div className="space-y-2">
              <div className="flex justify-between text-xs font-semibold text-slate-600">
                <span>{isUk ? 'Тривалість відео' : 'Video Duration'}</span>
                <span className="font-mono text-green-650 font-bold">{duration} сек (sec)</span>
              </div>
              <input
                type="range"
                min="1"
                max="60"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value, 10))}
                disabled={isRendering}
                className="w-full h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-green-550"
              />
              <div className="flex justify-between text-[9px] text-slate-400 font-mono">
                <span>1с</span>
                <span>30с</span>
                <span>60с</span>
              </div>
            </div>
          </div>

          {/* Upload container */}
          <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 shadow-xs space-y-4">
            <div className="flex items-center gap-2 text-slate-800 font-bold border-b border-slate-100 pb-3">
              <Upload className="w-4 h-4 text-green-500" />
              <h2 className="text-sm uppercase tracking-wider">{isUk ? 'Завантаження коду анімації' : 'Upload Animation Code'}</h2>
            </div>

            {/* Quick Demo Sample Button */}
            <button
              type="button"
              onClick={() => {
                setFileName("sample_promo_banner.html");
                setFileSize(8420);
                setWidth(1920);
                setHeight(1080);
                setFps(60);
                setDuration(5);
                setError(null);
              }}
              className="w-full mb-3 py-2.5 px-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-emerald-800 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
            >
              <Sparkles className="w-4 h-4 text-emerald-600 animate-pulse" />
              <span>{isUk ? "⚡ Спробувати на демо-прикладі в 1 клік (Рендер HTML-банера 1080p 60FPS)" : "⚡ Try 1-click Demo Sample (Render HTML Banner 1080p)"}</span>
            </button>

            {!fileName ? (
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 hover:border-green-400 rounded-lg py-8 px-4 cursor-pointer transition-all bg-slate-50/[0.3] hover:bg-green-50/[0.04] group">
                <Upload className="w-8 h-8 text-slate-400 group-hover:text-green-500 transition-colors mb-3" />
                <span className="text-xs font-bold text-slate-700 text-center mb-1">
                  {isUk ? 'Оберіть HTML або ZIP-архів з асетами' : 'Select HTML or ZIP archive with assets'}
                </span>
                <span className="text-[10px] text-slate-400 text-center">
                  {isUk ? 'ZIP має містити promo.html або index.html в корені' : 'ZIP should contain promo.html or index.html in the root'}
                </span>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".html,.zip"
                  className="hidden"
                />
              </label>
            ) : (
              <div className="bg-slate-50 border border-slate-100 rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="bg-green-100 text-green-600 rounded p-2 mt-0.5">
                    <Film className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-800 truncate">{fileName}</p>
                    <p className="text-[10px] text-slate-400 font-mono">{formatBytes(fileSize || 0)}</p>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-1 border-t border-slate-150">
                  <button
                    type="button"
                    onClick={handleClear}
                    disabled={isRendering}
                    className="py-1 px-2.5 text-[10px] font-bold text-red-500 hover:bg-red-50 rounded transition-colors cursor-pointer"
                  >
                    {isUk ? 'Очистити' : 'Clear'}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 flex gap-2 items-start text-xs">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-500" />
                <p className="font-mono">{error}</p>
              </div>
            )}

            {/* Submit Render Button */}
            <button
              type="button"
              onClick={handleStartRender}
              disabled={isRendering || !file}
              className={`w-full py-2.5 rounded font-mono text-xs font-bold transition-all uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer ${
                isRendering || !file
                  ? 'bg-slate-100 border border-slate-200 text-slate-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white shadow-xs'
              }`}
            >
              {isRendering ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>{isUk ? 'ВИКОНУЄТЬСЯ РЕНДЕРИНГ...' : 'RENDERING IN PROGRESS...'}</span>
                </>
              ) : (
                <>
                  <Play className="w-3.5 h-3.5 fill-current" />
                  <span>{isUk ? 'РОЗПОЧАТИ РЕНДЕРИНГ' : 'START RENDERING'}</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Live Logs & Preview Column */}
        <div className="lg:col-span-7 space-y-6">
          
          {/* Rendering Progress Card */}
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 sm:p-6 shadow-md text-slate-100 flex flex-col h-[400px]">
            <div className="flex items-center justify-between border-b border-slate-850 pb-3 mb-4 shrink-0">
              <div className="flex items-center gap-2">
                <Monitor className="w-4 h-4 text-green-400" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-300 font-mono">
                  {isUk ? 'Монітор процесу рендерингу' : 'Rendering Process Monitor'}
                </span>
              </div>
              {isRendering && (
                <span className="text-[10px] bg-green-500/10 border border-green-500/20 px-2 py-0.5 rounded text-green-400 font-mono animate-pulse">
                  Rendering
                </span>
              )}
            </div>

            {/* Log Panel (Console emulation) */}
            <div className="flex-1 min-h-0 bg-slate-950 rounded border border-slate-850 p-4 font-mono text-xs overflow-y-auto space-y-1.5 text-slate-400 leading-relaxed">
              {progressLogs.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-600 select-none space-y-2">
                  <Film className="w-8 h-8 opacity-40 animate-pulse" />
                  <p className="text-[10px]">{isUk ? 'Очікування завантаження файлу та запуску...' : 'Waiting for file upload and start...'}</p>
                </div>
              ) : (
                <>
                  {progressLogs.map((log, idx) => {
                    let colorClass = 'text-slate-450';
                    if (log.includes('Помилка') || log.includes('Error')) {
                      colorClass = 'text-red-400 font-bold';
                    } else if (log.includes('успішно') || log.includes('successfully') || log.includes('complete')) {
                      colorClass = 'text-green-400 font-bold';
                    } else if (log.includes('Рендеринг кадра')) {
                      colorClass = 'text-sky-350';
                    }
                    
                    return (
                      <div key={idx} className={colorClass}>
                        {log}
                      </div>
                    );
                  })}
                  <div ref={logEndRef} />
                </>
              )}
            </div>

            {/* Progress bar */}
            {progressLogs.length > 0 && (
              <div className="mt-4 space-y-1.5 shrink-0">
                <div className="flex justify-between text-[10px] font-mono text-slate-400">
                  <span>{isUk ? 'Прогрес:' : 'Progress:'}</span>
                  <span>{progressPercent}%</span>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className="bg-green-550 h-full rounded-full transition-all duration-350"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Render Result Card */}
          {downloadUrl && (
            <div className="bg-white border border-green-200 rounded-lg p-5 sm:p-6 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-4 animate-fade-in">
              <div className="flex items-start gap-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 text-green-600 shrink-0">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800">
                    {isUk ? 'Відеофайл успішно створено!' : 'Video file generated successfully!'}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {isUk 
                      ? `Анімацію відрендерено в роздільну здатність ${width}x${height} (${fps} FPS) тривалістю ${duration} сек.`
                      : `Animation rendered in resolution ${width}x${height} (${fps} FPS) lasting ${duration} sec.`}
                  </p>
                </div>
              </div>

              <a
                href={downloadUrl}
                download="rendered-animation.mp4"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 py-2 px-5 bg-green-600 hover:bg-green-700 text-white rounded font-mono text-xs font-bold transition-all shadow-xs shrink-0 cursor-pointer"
              >
                <Download className="w-4 h-4" />
                <span>{isUk ? 'ЗАВАНТАЖИТИ MP4' : 'DOWNLOAD MP4'}</span>
              </a>
            </div>
          )}
        </div>

      </div>

    </div>
  );
}
